import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Tab, Button, Form, Nav, Breadcrumb } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import TabTableWithSearch from './TabTableWithSearch';
import refreshIcon from "assets/svg/refresh-green.svg";
import { IconButton } from "@mui/material";
import SensorIcon from "components/gateways/SensorIcon";
import { TimeRangeSelection } from "components/common/TimeRangeDropdown";
import moment from "moment";
import { CHART_CUSTOM_RANGE_OPTION } from "constant";
import { 
    getGatewayAlertNotification,
    getOneRegistry,
    getRegistryConfig,
    getGwAndLDSUStatus,
    getAllLastBusReading,
    getSensorLastReading
} from "service/gatewayService";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { LoadingModal } from "components/modals/ModalTemplate";
import { isHttpSuccess, convertSecondsToPrometheusTime } from "utils/functions";
import { get } from "lodash";
import { useOrganisation } from "hooks/organization";
import { getLDSBusInfo } from "utils/gatewayFunctions";
import { getCalendarFormat } from "utils/datetime";
import {
    getTagDeviceSAID,
    getTagDeviceUID,
    isRoomOrZoneByName,
    isGroupOrHubByName,
    isSensorFromEntityDevice,
    selectEntityUUID,
} from "./common/deviceUtils";
import { getSensorsControllersTabType } from "./common/tabUtils";
import { renderSensorsControllersFilters } from "./common/filters";
import { getControllerStatusInfo } from "./common/controllerStatus";
import { getControllerStatusFromReading2 } from "./common/controllerReadingUtils";
import { renderLastActiveCellCommon, renderStatusCellCommon } from "./common/statusRenderer";
import { queryTagStatusFromUIDSAIDsWithTimestamp } from "service/networkService";
import { buildPromQLQueryFromUIDSAIDsMap, selectUIDSAIDsForUID } from "./common/promqlUtils";
import {
    createDefaultLast24HoursTimeRange,
    getPrometheusTimeRangeForSensorReadings,
    pickBestSensorBusReadingForSAID,
    mergeSensorLastReadingWithMinMaxFallback,
} from "./common/sensorStatsUtils";
import {
    formatSensorReadingCellValue,
    formatSensorMinMaxCellValue,
    getSensorUnit,
} from "./common/sensorValueFormatter";
import { fetchTags as fetchTagsCommon } from "./common/tagService";
import {
    updateAlertStatus as updateAlertStatusCommon,
    returnCheckboxStatus as returnCheckboxStatusCommon,
    returnDisabledStatus as returnDisabledStatusCommon
} from "./common/alertUtils";
import { getRowToForContext, type NetworkRowToContext } from "./common/getRowTo";
import { getNetworkColumnLabel } from "./common/columnLabelUtils";

type ColumnRenderer = (row: any) => React.ReactNode;

// Helper to collect all sensors and actuators for a group across buses.
// This keeps the main fetchGroupData function simpler and reduces its
// cognitive complexity while preserving the existing behaviour.
const buildGroupSensorsAndActuators = (
    devices: any[],
    configList: any[],
    statusDevices: any[]
) => {
    const allSensors: any[] = [];
    const allActuators: any[] = [];

    for (let busNumber = 1; busNumber <= 3; busNumber++) {
        const { sensors: busSensors, actuators: busActuators } = getLDSBusInfo(
            busNumber.toString(),
            devices,
            configList,
            statusDevices
        );

        // Filter out only sensors from zone/room/group/hub entity devices. Include VIRTUAL/aux-hub sensors.
        const actualSensors = busSensors.filter((s: any) => !isSensorFromEntityDevice(s, devices));
        // At group level, trust per‑channel CLS/type from getLDSBusInfo /
        // getActuatorList and do not re‑filter by device OBJ. This avoids
        // hiding valid actuator channels on auxiliary hubs.
        const filteredActuators = busActuators;

        allSensors.push(...actualSensors);
        allActuators.push(...filteredActuators);
    }

    return { sensors: allSensors, actuators: allActuators };
};


const renderOnOffHighlighted = (text: string) => {
    if (!text) return "-";
    const parts = String(text).split(/\b(on|off)\b/i);
    let keyCounter = 0;

    return (
        <span>
            {parts.map((part) => {
                const lower = part.toLowerCase();
                const isTarget = lower === "on" || lower === "off";
                const key = `onoff-${keyCounter++}`;
                return isTarget ? (
                    <span key={key} style={{ color: "#00cbaf" }}>
                        {part}
                    </span>
                ) : (
                    <span key={key}>{part}</span>
                );
            })}
        </span>
    );
};

const createLocalSensorNameRenderer = (
    columnKey: string,
    tabKey: string,
    onRowClick: (row: any, tabKey: string) => void,
    getRowTo?: (row: any, tabKey: string) => string
): ColumnRenderer => {
    return (row: any) => {
        const to = getRowTo?.(row, tabKey) ?? "#";
        return (
            <Link
                to={to}
                onClick={to === "#" ? (e) => {
                    e.preventDefault();
                    onRowClick(row, tabKey);
                } : undefined}
            >
                <span style={{ display: "flex", alignItems: "center" }}>
                    <SensorIcon
                        sensorType={row.type ?? row.sensor_type ?? "Generic Analog Input"}
                        style={{ marginRight: 15 }}
                    />
                    <span>{row.sensor_name ?? row[columnKey]}</span>
                </span>
            </Link>
        );
    };
};

const createControllerNameRenderer = (
    columnKey: string,
    tabKey: string,
    onRowClick: (row: any, tabKey: string) => void,
    getRowTo?: (row: any, tabKey: string) => string
): ColumnRenderer => {
    return (row: any) => {
        const to = getRowTo?.(row, tabKey) ?? "#";
        return (
            <Link
                to={to}
                onClick={to === "#" ? (e) => {
                    e.preventDefault();
                    onRowClick(row, tabKey);
                } : undefined}
            >
                {row[columnKey]}
            </Link>
        );
    };
};

const createReadingRenderer = (col: string): ColumnRenderer => {
    return (row: any) => {
        // Align with NetworkDetails: format from raw readings value so display matches sort value.
        return (
            <span style={{ color: "#00d1b2" }}>
                {formatSensorReadingCellValue(row, row[col])}
            </span>
        );
    };
};

const createMinMaxRenderer = (col: string): ColumnRenderer => {
    return (row: any) => (
        <span>
            {row[col] ?? "-"}
        </span>
    );
};

const createLastActiveRenderer = (
    col: string,
    tabKey: string,
    onRefreshSensor: (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        row: any
    ) => void
): ColumnRenderer => {
    return (row: any) =>
        renderLastActiveCellCommon({
            row,
            col,
            tabKey,
            onRefreshSensor,
        });
};

const createStatusRenderer = (
    col: string,
    tabKey: string,
    onRefreshController: (e: React.MouseEvent, controllerName: string) => void
): ColumnRenderer => {
    return (row: any) => {
        const statusValue = row[col];
        return renderStatusCellCommon({
            statusValue,
            tabKey,
            controllerName: row.name,
            onRefreshController,
            renderOnOffHighlighted,
            refreshButtonExtraClassName: "refresh-controller",
        });
    };
};

const createAlertRenderer = (
    col: string,
    onToggleAlert: (ldsuUuid: string, said: number) => void,
    isChecked: (ldsuUuid: string, said: number) => boolean,
    isDisabled: (ldsuUuid: string, said: number) => boolean
): ColumnRenderer => {
    return (row: any) => (
        <HoverAuthorizeTooltip permission="gateway:update">
            <div>
                <Form.Check
                    type="switch"
                    id={`switch-${row.ldsu_uuid}-${row.SAID}`}
                    label=""
                    checked={isChecked(row.ldsu_uuid, row.SAID)}
                    disabled={isDisabled(row.ldsu_uuid, row.SAID)}
                    onChange={() => onToggleAlert(row.ldsu_uuid, row.SAID)}
                />
            </div>
        </HoverAuthorizeTooltip>
    );
};

const tabKeys = [
    "sensors",
    "controllers",
];

const tabLabels: { [key: string]: string } = {
    sensors: "Sensors in this Group",
    controllers: "Actuators in this Group",
};

interface GroupDetailsProps {
    networkId?: string;
    groupId?: string;
    showBreadcrumbs?: boolean;
    customTitle?: string;
}

const GroupDetails = ({ 
    networkId, 
    groupId, 
    showBreadcrumbs = true,
    customTitle 
}: GroupDetailsProps): JSX.Element => {
    const params = useParams<{ id: string; groupId: string }>();
    
    const id = networkId ?? params.id;
    const groupIdFromParams = groupId ?? params.groupId;
    
    const [activeTab, setActiveTab] = useState("sensors");
    const [network, setNetwork] = useState<any>(null);
    const [group, setGroup] = useState<any>(null);
    const [groupName, setGroupName] = useState(groupIdFromParams || "");
    const [isGroupNameEditing, setIsGroupNameEditing] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [sensors, setSensors] = useState<any[]>([]);
    const [controllers, setControllers] = useState<any[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const history = useHistory();
    const { currentOrgId } = useOrganisation();
    const groupTagUIDSAIDsRef = useRef<Map<string, Set<number>>>(new Map());
    useNetworkIdRedirect(id);
    
    // Sensor-specific state
    const [selectedSensorTypes, setSelectedSensorTypes] = useState<string[]>([]);
    const [selectedActuatorTypes, setSelectedActuatorTypes] = useState<string[]>([]);
    const timeRange = useMemo<TimeRangeSelection>(
        () => createDefaultLast24HoursTimeRange(),
        []
    );

    // Alert state management
    const [alertUUIDList, setAlertUUIDList] = useState<any[]>([]);
    const [isAlertLoading, setIsAlertLoading] = useState(false);
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();

    // Helper to render a timestamp in a consistent calendar format
    const renderDateTime = useCallback((timestamp?: number | string): string => {
        if (!timestamp) {
            return "";
        }
        return moment(timestamp, "X").calendar(getCalendarFormat());
    }, []);
    
    // Process sensors for display
    const processedSensors = useMemo(() => {
        return sensors.map((sensor: any) => {
            // Extract minmax from sensor to avoid duplicate column
            const { minmax, ...sensorWithoutMinmax } = sensor;
            const ldsuUUID = sensor.ldsu_uuid ?? sensor.UID ?? "";
            const saidValue = sensor.SAID ?? 0;
            const resolvedUnit = getSensorUnit(sensor);
            return {
                // Display fields
                name: sensor.sensor_name ?? sensor.name ?? "-",
                sensor_name: sensor.sensor_name ?? sensor.name ?? "-",
                sensor_type: sensor.sensor_type ?? sensor.type ?? "-",
                type: sensor.sensor_type ?? sensor.type ?? "-",
                // Raw value; actual formatting handled by reading renderer
                reading: sensor.readings?.value ?? "-",
                readingSortValue:
                    sensor.readings?.value != null &&
                    sensor.readings?.value !== "-" &&
                    !Number.isNaN(Number(sensor.readings.value))
                        ? Number(sensor.readings.value)
                        : Number.POSITIVE_INFINITY,
                minMax: sensor.readings?.minmax
                    ? formatSensorMinMaxCellValue({
                        readings: sensor.readings,
                        accuracy: sensor.accuracy,
                        unit: resolvedUnit,
                    })
                    : "-",
                minMaxSortValue: (() => {
                    const arr = sensor.readings?.minmax;
                    if (!arr || !Array.isArray(arr) || arr.length < 1) return Number.POSITIVE_INFINITY;
                    const v = arr[0];
                    if (v == null || v === "-" || Number.isNaN(Number(v))) return Number.POSITIVE_INFINITY;
                    return Number(v);
                })(),
                lastActive: renderDateTime(sensor.readings?.timestamp),

                ldsu_uuid: ldsuUUID,
                SAID: saidValue,
                ldsu_name: sensor.ldsu_name || sensor.name || "-",
                alert: "",
                // For alert column sorting: 1 if enabled, 0 if disabled
                alertSortValue: returnCheckboxStatusCommon(
                    alertUUIDList as any,
                    ldsuUUID,
                    saidValue
                )
                    ? 1
                    : 0,
                accuracy: sensor.accuracy,
                unit: resolvedUnit,
                readings: sensor.readings,

                ...sensorWithoutMinmax
            };
        });
    }, [sensors, renderDateTime, alertUUIDList]);

    // Filter sensors by selected types
    const filteredSensorList = useMemo(() => {
        if (!processedSensors.length || selectedSensorTypes.length === 0) {
            return processedSensors;
        }
        return processedSensors.filter(sensor => 
            selectedSensorTypes.includes(sensor.type)
        );
    }, [processedSensors, selectedSensorTypes]);
    
    // Sensor types for filtering
    const sensorTypes = useMemo(() => {
        if (activeTab !== "sensors" || !processedSensors || processedSensors.length === 0) return [];
        const types: { [id: string]: string } = {};
        for (const row of processedSensors) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [processedSensors, activeTab]);

    // Derive actuator types for filtering
    const actuatorTypes = useMemo(() => {
        if (!controllers.length) return [];
        const types: { [id: string]: string } = {};
        for (const row of controllers) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        return Object.keys(types).map((id) => ({ id, name: id }));
    }, [controllers]);

    // Filter controllers by selected actuator types
    const filteredControllers = useMemo(() => {
        if (!controllers.length || selectedActuatorTypes.length === 0) {
            return controllers;
        }
        return controllers.filter((actuator: any) =>
            selectedActuatorTypes.includes(actuator.type)
        );
    }, [controllers, selectedActuatorTypes]);

    // Process controllers for display (status from last bus reading only)
    const processedControllers = useMemo(() => {
        return filteredControllers.map((actuator: any) => {
            const { status } = getControllerStatusInfo(actuator);
            const lastActiveTs = actuator.readings?.timestamp ?? actuator.timestamp;
            return {
                name: actuator.name || "-",
                type: actuator.type || "-",
                status,
                lastActive: lastActiveTs ? renderDateTime(lastActiveTs) : "",
                UID: actuator.UID,
                said: actuator.said,
            };
        });
    }, [filteredControllers, renderDateTime]);

    // Alert management functions
    const fetchAlertData = useCallback(async () => {
        const ldsuGatewayId = id; // Use panlNetworkId from route params
        
        try {
            const alertResponse = await getGatewayAlertNotification(ldsuGatewayId);
            if (isHttpSuccess(alertResponse.status)) {
                const alerts = alertResponse.data || [];
                // Transform alert data to match sensor structure (device_id -> source, enabled -> status)
                const transformedAlerts = alerts.map((alert: any) => ({
                    uuid: alert.uuid,
                    source: alert.device_id,
                    said: alert.said,
                    gatewayUUID: alert.gateway_id,
                    status: alert.enabled,
                    notice: alert.notice,
                }));
                setAlertUUIDList(transformedAlerts);
            }
        } catch (error) {
            console.error("Error fetching alert data:", error);
        }
    }, [id]);

    const updateAlertStatus = async (source: string, said: number) => {
        await updateAlertStatusCommon(
            alertUUIDList as any,
            source,
            String(said),
            setAlertUUIDList as any,
            setIsAlertLoading
        );
    };

    const returnCheckboxStatus = (source: string, said: number) => {
        return returnCheckboxStatusCommon(alertUUIDList as any, source, said);
    };

    const returnDisabledStatus = (source: string, said: number) => {
        return returnDisabledStatusCommon(alertUUIDList as any, source, said);
    };

    /**
     * Fetches tags from API
     * @param organizationId - Organization ID
     * @param tagValue - Optional tag value filter (e.g., group name)
     * @returns Array of tag objects
     */
    const fetchTags = async (organizationId: string, tagValue?: string) =>
        fetchTagsCommon(organizationId, { value: tagValue });

    const extractTagUIDs = (tags: any[]): Set<string> => {
        const uids = new Set<string>();
        for (const tag of tags) {
            if (tag.devices && Array.isArray(tag.devices)) {
                for (const device of tag.devices) {
                    const uid = getTagDeviceUID(device);
                    if (uid) {
                        uids.add(uid);
                    }
            }
            }
        }
        return uids;
    };

    const extractTagUIDSAIDs = (tags: any[]): Map<string, Set<number>> => {
        const uidSaidMap = new Map<string, Set<number>>();

        const addDeviceToMap = (device: any) => {
            const uid = getTagDeviceUID(device);
            if (!uid) {
                return;
            }

            const said = getTagDeviceSAID(device);
            let saidSet = uidSaidMap.get(uid);

            if (!saidSet) {
                saidSet = new Set<number>();
                uidSaidMap.set(uid, saidSet);
            }

            saidSet.add(said);
        };

        for (const tag of tags) {
            if (!tag.devices || !Array.isArray(tag.devices)) {
                continue;
            }

            for (const device of tag.devices) {
                addDeviceToMap(device);
            }
        }

        return uidSaidMap;
    };

    /**
     * Extracts device UIDs from registry API response
     * @param registryData - Registry API response data
     * @returns Set of device UIDs
     */
    const extractRegistryUIDs = (registryData: any): Set<string> => {
        const uids = new Set<string>();
        if (registryData?.info?.devices && Array.isArray(registryData.info.devices)) {
            for (const device of registryData.info.devices) {
                if (device?.UID) {
                    uids.add(String(device.UID));
                } else if (Array.isArray(device) && device.length > 0) {
                    uids.add(String(device[0]));
                } else if (typeof device === "string") {
                    uids.add(device);
                }
            }
        }
        return uids;
    };

    const filterGroupSensorsByMembership = (
        sensorsWithReadings: any[],
        groupUIDs: Set<string>,
        groupTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return sensorsWithReadings.filter((sensor: any) => {
            const sensorUID = sensor.ldsu_uuid || sensor.UID || "";
            const sensorSAID =
                sensor.SAID !== undefined && sensor.SAID !== null ? Number(sensor.SAID) : 0;

            if (!groupUIDs.has(sensorUID)) {
                return false;
            }

            const allowedSAIDs = groupTagUIDSAIDs.get(sensorUID);
            if (allowedSAIDs && !allowedSAIDs.has(sensorSAID)) {
                return false;
            }

            const device = uidToDeviceMap.get(sensorUID);
            if (!device) {
                return false;
            }

            // Check NAME/NICK field first (more reliable when same UID appears in tags API)
            const name = device?.NAME;
            const nick = device?.NICK;
            if (isGroupOrHubByName(name) || isGroupOrHubByName(nick)) {
                // Exclude groups/hubs
                return false;
            }
            if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) {
                // Exclude rooms/zones
                return false;
            }

            // Fallback to OBJ value check.
            // IMPORTANT:
            // - Do NOT exclude auxiliary hubs here so that sensors coming from
            //   LDSBus 4in1 (and similar) devices still appear in the Group
            //   Details sensor list when tagged (e.g. Group_1 in the UI).
            // - We only exclude entity-like devices (OBJ 45057) which represent
            //   logical groups/hubs rather than physical sensors.
            const deviceObj = device?.OBJ;
            return !(
                deviceObj === "45057" ||
                deviceObj === 45057
            );
        });
    };

    const filterGroupActuatorsByMembership = (
        allActuators: any[],
        groupUIDs: Set<string>,
        groupTagUIDSAIDs: Map<string, Set<number>>,
        uidToDeviceMap: Map<string, any>
    ): any[] => {
        return allActuators.filter((actuator: any) => {
            const actuatorUID = actuator.UID || "";
            const actuatorSAID =
                actuator.said !== undefined && actuator.said !== null
                    ? Number(actuator.said)
                    : 0;

            if (!groupUIDs.has(actuatorUID)) {
                return false;
            }

            const allowedSAIDs = groupTagUIDSAIDs.get(actuatorUID);
            if (allowedSAIDs && allowedSAIDs.size > 0 && !allowedSAIDs.has(actuatorSAID)) {
                return false;
            }

            const device = uidToDeviceMap.get(actuatorUID);
            if (!device) {
                return false;
            }

            // Check NAME/NICK field first (more reliable when same UID appears in tags API)
            const name = device?.NAME;
            const nick = device?.NICK;
            if (isGroupOrHubByName(name) || isGroupOrHubByName(nick)) {
                // Exclude groups/hubs
                return false;
            }
            if (isRoomOrZoneByName(name) || isRoomOrZoneByName(nick)) {
                // Exclude rooms/zones
                return false;
            }

            // Fallback to OBJ value check.
            // IMPORTANT:
            // - Do NOT exclude auxiliary hubs here so that actuators coming from
            //   LDSBus / IO‑controller devices still appear in the Group Details
            //   when tagged (e.g. g11 where the Groups list shows 1 actuator).
            // - We only exclude entity‑like devices (OBJ 45057) which represent
            //   logical groups/hubs rather than physical actuators.
            const deviceObj = device?.OBJ;
            return !(
                deviceObj === "45057" ||
                deviceObj === 45057
            );
        });
    };

    // Map readings to sensors (helper function for refresh)
    const mapSensorsWithReadings = useCallback(async (sensorsList: any[], timeRange?: string, offset?: string) => {
        if (!id || !sensorsList.length) return sensorsList;
        
        try {
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(
                    getAllLastBusReading(
                        id,
                        busNumber.toString(),
                        timeRange,
                        offset
                    )
                );
            }
            const readingResults = await Promise.all(readingPromises);
            const allReadings: any[] = [];
            for (const result of readingResults) {
                if (isHttpSuccess(result.status)) {
                    allReadings.push(...(result.data?.devices || []));
                }
            }

            return sensorsList.map((sensor: any) => {
                const bestReading = pickBestSensorBusReadingForSAID(
                    sensor,
                    allReadings
                );

                if (bestReading) {
                    sensor.readings = {
                        value: bestReading.value,
                        highest: bestReading.highest,
                        lowest: bestReading.lowest,
                        timestamp: bestReading.timestamp,
                        minmax: [bestReading.highest, bestReading.lowest],
                    };
                }

                return sensor;
            });
        } catch (error) {
            console.error("GroupDetails: Error mapping sensor readings:", error);
            return sensorsList;
        }
    }, [id]);

    // Fetch group data
    const fetchGroupData = useCallback(async () => {
        if (!id || id === 'undefined' || !groupIdFromParams || !currentOrgId) {
            setIsPageLoading(false);
            return;
        }

        try {
            setIsPageLoading(true);
            // Fetch tags for this organisation (optionally filtered by value)
            // and then STRICTLY filter by exact group tag value "group/<name>".
            // This ensures that "Group_1" and "group_1" remain distinct tags
            // even if the backend tag lookup is case‑insensitive or fuzzy.
            const expectedTagValue = `group/${groupIdFromParams}`;
            const rawGroupTags = await fetchTags(currentOrgId, expectedTagValue);
            const groupTags = Array.isArray(rawGroupTags)
                ? rawGroupTags.filter((tag: any) => tag?.value === expectedTagValue)
                : [];
            const groupTagUIDs = extractTagUIDs(groupTags);
            const groupTagUIDSAIDs = extractTagUIDSAIDs(groupTags);
            groupTagUIDSAIDsRef.current = groupTagUIDSAIDs;
            
            const [registryRes, configRes, statusRes] = await Promise.all([
                getOneRegistry(id),
                getRegistryConfig(id),
                getGwAndLDSUStatus(id)
            ]);

            if (!isHttpSuccess(registryRes.status) || 
                !isHttpSuccess(configRes.status) || 
                !isHttpSuccess(statusRes.status)) {
                setIsPageLoading(false);
                return;
            }

            const devices = registryRes.data?.info?.devices || [];
            const configList = configRes.data || [];
            const statusDevices = statusRes.data?.devices || [];
            const registryUIDs = extractRegistryUIDs(registryRes.data);
            
            const groupUIDs = new Set<string>();
            for (const uid of Array.from(groupTagUIDs)) {
                if (registryUIDs.has(uid)) {
                    groupUIDs.add(uid);
                }
            }
            
            const { sensors: allSensors, actuators: allActuators } =
                buildGroupSensorsAndActuators(devices, configList, statusDevices);

            const selectedTimeRange = timeRange;
            let lastTimeValue = Math.round(
                moment.duration(selectedTimeRange.endDate.diff(selectedTimeRange.startDate)).asSeconds()
            );
            let offsetTime: string | undefined;

            if (selectedTimeRange.range === CHART_CUSTOM_RANGE_OPTION) {
                lastTimeValue = Math.round(
                    moment.duration(moment().diff(selectedTimeRange.endDate)).asSeconds()
                );

                offsetTime = convertSecondsToPrometheusTime(lastTimeValue);
                lastTimeValue = moment
                    .duration(selectedTimeRange.endDate.diff(selectedTimeRange.startDate))
                    .asSeconds();
            }

            const lastTime = convertSecondsToPrometheusTime(lastTimeValue);

            // Fetch readings for sensors using the current time range
            const sensorsWithReadings = await mapSensorsWithReadings(
                allSensors,
                lastTime,
                offsetTime
            );

            // Filter sensors and actuators that belong to this group only
            // Build UID to device map (includes OBJ, NAME, NICK for identification)
            const uidToDeviceMap = new Map<string, any>();
            for (const device of devices) {
                const deviceUID = device?.UID || device?.uid || "";
                if (deviceUID) {
                    uidToDeviceMap.set(String(deviceUID), device);
                }
            }
            
            const groupSensors = filterGroupSensorsByMembership(
                sensorsWithReadings || [],
                groupUIDs,
                groupTagUIDSAIDs,
                uidToDeviceMap
            );
            
            const groupActuators = filterGroupActuatorsByMembership(
                allActuators,
                groupUIDs,
                groupTagUIDSAIDs,
                uidToDeviceMap
            );
            const groupUuid = selectEntityUUID(groupUIDs, devices, groupIdFromParams, "group");
            
            let groupStatus = "Unknown";
            let groupTimestamp: number | null = null;
            if (currentOrgId && id && groupTagUIDSAIDsRef.current.size > 0) {
                try {
                    const groupUIDSAIDsForStatus =
                        selectUIDSAIDsForUID(groupTagUIDSAIDsRef.current, groupUuid);
                    const result = await queryTagStatusFromUIDSAIDsWithTimestamp(
                        id,
                        (gw: string) =>
                            buildPromQLQueryFromUIDSAIDsMap(gw, groupUIDSAIDsForStatus)
                    );
                    groupStatus = result.status;
                    groupTimestamp = result.timestamp ?? null;
                } catch (error) {
                    console.error("Error querying group status by UID:", error);
                }
            }

            const groupData = {
                name: groupIdFromParams,
                uuid: groupUuid,
                sensors: groupSensors.length,
                controllers: groupActuators.length,
                status: groupStatus,
                // Store the time when the group status was last evaluated
                timestamp: groupTimestamp,
            };

            setGroup(groupData);
            setGroupName(groupIdFromParams);
            setSensors(groupSensors);
            // Merge actuator status from getAllLastBusReading (same source as refresh)
            const mergeActuatorWithDevices = (a: any, devices: any[]) => {
                const device = devices.find((d: any) => d.UID === a.UID);
                if (!device) return a;
                const status = get(device.value, `[${a.said || 0}]`, null);
                return { ...a, readings: { status, timestamp: device.ts } };
            };
            const mergeActuatorsWithReadings = async (actuatorsList: any[]) => {
                const readingPromises = [];
                for (let busNumber = 1; busNumber <= 3; busNumber++) {
                    readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
                }
                const results = await Promise.all(readingPromises);
                const allDevices: any[] = [];
                for (const r of results) {
                    if (isHttpSuccess(r.status) && r.data?.devices) allDevices.push(...r.data.devices);
                }
                return actuatorsList.map((a) => mergeActuatorWithDevices(a, allDevices));
            };
            const groupActuatorsWithReadings = await mergeActuatorsWithReadings(groupActuators);
            setControllers(groupActuatorsWithReadings);

            // Set network info
            setNetwork({
                name: registryRes.data?.name || "Network",
                id: id
            });

            // Fetch alert data
            await fetchAlertData();
            setIsPageLoading(false);
        } catch (error) {
            console.error("Error fetching group data:", error);
            setIsPageLoading(false);
        }
    }, [id, groupIdFromParams, currentOrgId, fetchAlertData, mapSensorsWithReadings, timeRange]);

    // Dedicated function to update only the status of the group
    const updateGroupStatus = useCallback(async () => {
        if (!currentOrgId || !id || !groupIdFromParams) {
            return;
        }

        setIsRefreshing(true);
        try {
            const groupUIDSAIDsForStatus =
                selectUIDSAIDsForUID(groupTagUIDSAIDsRef.current, group?.uuid);
            const result = await queryTagStatusFromUIDSAIDsWithTimestamp(
                id,
                (gw: string) =>
                    buildPromQLQueryFromUIDSAIDsMap(gw, groupUIDSAIDsForStatus)
            );
            setGroup((prevGroup: any) => {
                if (!prevGroup) return prevGroup;
                return {
                    ...prevGroup,
                    status: result.status,
                    // Update timestamp from backend where available, otherwise keep previous
                    timestamp: result.timestamp ?? prevGroup.timestamp ?? null,
                };
            });
        } catch (error) {
            console.error("Error updating group status:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, groupIdFromParams, currentOrgId, group]);

    useEffect(() => {
        setIsPageLoading(true);
        fetchGroupData();
    }, [fetchGroupData]);

    // Refresh all sensor readings
    const refreshAllSensorReadings = useCallback(async (timeRange?: string, offset?: string) => {
        if (!id || !sensors.length) return;
        
        setIsRefreshing(true);
        try {
            const sensorsWithReadings = await mapSensorsWithReadings(
                sensors,
                timeRange,
                offset
            );
            setSensors(sensorsWithReadings);
        } catch (error) {
            console.error("GroupDetails: Error refreshing sensor readings:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, sensors, mapSensorsWithReadings]);

    // Refresh a single sensor's reading using getSensorLastReading
    const refreshOneSensor = async (ldsuUUID: string, SAID: number) => {
        if (!id || !ldsuUUID) return;

        try {
            const { lastTime, offsetTime } =
                getPrometheusTimeRangeForSensorReadings(timeRange);
            const { data, status } = await getSensorLastReading(
                id,
                ldsuUUID,
                lastTime,
                offsetTime
            );

            if (isHttpSuccess(status)) {
                const rawValueCandidateRaw = get(data.value, `[${SAID}]`, "-");
                const highestCandidateRaw = get(data.max, `[${SAID}]`, "-");
                const lowestCandidateRaw = get(data.min, `[${SAID}]`, "-");

                const valueCandidate = rawValueCandidateRaw ?? "-";
                const highestCandidate = highestCandidateRaw ?? "-";
                const lowestCandidate = lowestCandidateRaw ?? "-";

                setSensors((prevSensors: any[]) => {
                    return prevSensors.map((s: any) => {
                        if (String(s.ldsu_uuid) === String(ldsuUUID) && String(s.SAID) === String(SAID)) {
                            const prevReadings = s.readings ?? {};

                            const updatedReading =
                                mergeSensorLastReadingWithMinMaxFallback(
                                    prevReadings,
                                    {
                                        highest: highestCandidate,
                                        lowest: lowestCandidate,
                                        value: valueCandidate,
                                        timestamp: data?.ts,
                                    }
                                );

                            return { ...s, readings: updatedReading };
                        }
                        return s;
                    });
                });
            }
        } catch (error) {
            console.error("GroupDetails: Error refreshing sensor reading:", error);
        }
    };

    // Handle refresh sensor click
    const handleRefreshSensor = async (
        e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
        sensor: any
    ) => {
        const target = e.currentTarget as HTMLButtonElement;
        if (!target) return;
        
        const sensorId = `${sensor.ldsu_uuid}-${sensor.SAID}`;
        target.dataset.sensorId = sensorId;
        target.classList.add("refresh-state");
        
        try {
            await refreshOneSensor(sensor.ldsu_uuid, sensor.SAID);
        } finally {
            const element = document.querySelector<HTMLButtonElement>(
                `.refresh-sensor[data-sensor-id="${sensorId}"]`
            );
            if (element) {
                element.classList.remove("refresh-state");
                delete element.dataset.sensorId;
            }
        }
    };

    // Single-row controller refresh: per-device reading2. Bulk refresh still uses getAllLastBusReading.
    const refreshOneController = async (controllerUID: string, said: number) => {
        if (!id || !controllerUID) return;

        try {
            const { data, status } = await getSensorLastReading(id, controllerUID);
            if (!isHttpSuccess(status)) return;

            const parsed = getControllerStatusFromReading2(data, said);
            if (!parsed) return;

            const { status: updatedStatus, timestamp: updatedTimestamp } = parsed;

            setControllers((prevControllers: any[]) =>
                prevControllers.map((c: any) => {
                    if (c.UID === controllerUID && c.said === said) {
                        return {
                            ...c,
                            readings: {
                                value: updatedStatus,
                                status: updatedStatus,
                                timestamp: updatedTimestamp,
                            },
                        };
                    }
                    return c;
                })
            );
        } catch (error) {
            console.error("GroupDetails: Error refreshing controller status:", error);
        }
    };

    // Handle refresh controller click
    const handleRefreshController = async (
        e: React.MouseEvent,
        controllerName: string
    ) => {
        e.stopPropagation();

        const target = e.currentTarget as HTMLButtonElement | null;

        // Find the controller by name
        const controller = controllers.find((c: any) => c.name === controllerName);
        const controllerUID = controller?.UID;
        if (!controllerUID) {
            console.error("GroupDetails: Controller not found for refresh");
            return;
        }

        if (target) {
            target.classList.add("refresh-state");
        }

        try {
            await refreshOneController(controllerUID, controller?.said || 0);
        } finally {
            if (target) {
                target.classList.remove("refresh-state");
            }
        }
    };

    const mapControllersWithLatestReadings = (controllersList: any[], allDevices: any[]): any[] => {
        return controllersList.map((controller: any) => {
            const device = allDevices.find((d: any) => d.UID === controller.UID);
            if (!device) {
                return controller;
            }
            const updatedStatus = get(device.value, `[${controller.said || 0}]`, null);
            const updatedTimestamp = device.ts;
            return {
                ...controller,
                readings: {
                    value: updatedStatus,
                    status: updatedStatus,
                    timestamp: updatedTimestamp,
                },
            };
        });
    };

    // Refresh all controller statuses
    const refreshAllControllerStatuses = async () => {
        if (!id || !controllers.length) return;
        
        setIsRefreshing(true);
        try {
            // Check all buses (1-3) to get all controller readings
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(getAllLastBusReading(id, busNumber.toString()));
            }
            const readingResults = await Promise.all(readingPromises);
            
            // Collect all device readings from all buses
            const allDevices: any[] = [];
            for (const result of readingResults) {
                if (isHttpSuccess(result.status) && result.data.devices) {
                    allDevices.push(...result.data.devices);
                }
            }
            
            // Update all controllers with their latest readings
            setControllers((prevControllers: any[]) =>
                mapControllersWithLatestReadings(prevControllers, allDevices)
            );
        } catch (error) {
            console.error("GroupDetails: Error refreshing all controller statuses:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleAlertToggle = (ldsuUuid: string, said: number) => {
        const isChecked = returnCheckboxStatus(ldsuUuid, said);

        if (isChecked) {
            updateAlertStatus(ldsuUuid, said);
            return;
        }

        walletModalTrigger(() => updateAlertStatus(ldsuUuid, said));
    };

    const getEmptyMessageForTab = (tabKey: string): string => {
        if (tabKey === "sensors") {
            return "No Sensors to display";
        }

        if (tabKey === "controllers") {
            return "No Actuators to display";
        }

        return "No data to display";
    };

    const renderSensorReadingCell = (value: any, row: any) => {
        const formattedValue = formatSensorReadingCellValue(row, value);
        return <span style={{ color: "#00d1b2" }}>{formattedValue}</span>;
    };

    const renderSensorMinMaxCell = (value: any) => (
        <span>
            {value || "-"}
        </span>
    );

    const renderSensorLastActiveCell = (value: any, row: any) => (
        <span>
            {value}
            <IconButton
                disableRipple
                className={
                    isRefreshing
                        ? "mr-2 bigRefresh refresh-animation"
                        : "mr-2 bigRefresh"
                }
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", marginLeft: 8 }}
                aria-label="Refresh sensor reading"
                data-sensor-id={`${row.ldsu_uuid}-${row.SAID}`}
                onClick={(e) => {
                    e.stopPropagation();
                    handleRefreshSensor(e, row);
                }}
                onKeyDown={(e) => {
                    e.stopPropagation();
                    handleRefreshSensor(e, row);
                }}
            >
                <img
                    src={refreshIcon}
                    alt="refresh"
                />
            </IconButton>
        </span>
    );

    const renderSensorAlertCell = (value: any, row: any) => (
        <HoverAuthorizeTooltip permission="gateway:update">
            <div>
                <Form.Check
                    type="switch"
                    id={`switch-${row.ldsu_uuid}-${row.SAID}`}
                    label=""
                    checked={returnCheckboxStatus(
                        row.ldsu_uuid,
                        row.SAID
                    )}
                    disabled={returnDisabledStatus(
                        row.ldsu_uuid,
                        row.SAID
                    )}
                    onChange={() => handleAlertToggle(row.ldsu_uuid, row.SAID)}
                />
            </div>
        </HoverAuthorizeTooltip>
    );

    /* istanbul ignore next -- edit button is intentionally disabled in current UI flow */
    const toggleGroupNameEdit = () => {
        setIsGroupNameEditing(!isGroupNameEditing);
    };

    /* istanbul ignore next -- edit button is intentionally disabled in current UI flow */
    const toggleGroupNameUpdate = () => {
        setIsGroupNameEditing(false);
        if (group) {
            setGroup({ ...group, name: groupName });
        }
    };

    // Tab detection logic
    const getTabType = getSensorsControllersTabType;

    const handleRowClick = (row: any, tabType?: string) => {
        switch (tabType) {
            case 'sensors':
                history.push({
                    pathname: `/network-details/${id}/group-details/${groupIdFromParams}/sensor-details/${row.ldsu_uuid}-${row.SAID}`,
                    state: {
                        sensorDetails: row,
                        gatewayName: "Network Gateway",
                        portNumber: "1",
                        UUID: id,
                        sensorTimeRange: {
                            range: timeRange.range,
                            startDate: timeRange.startDate.valueOf(),
                            endDate: timeRange.endDate.valueOf(),
                        },
                    }
                });
                break;
            case 'controllers':
                if (row.UID != null && row.said != null) {
                    history.push(`/network-details/${id}/group-details/${groupIdFromParams}/controller-details/${row.UID}-${row.said}`);
                }
                break;
            default:
                break;
        }
    };

    const rowToContext: NetworkRowToContext = useMemo(
        () => ({ page: "group", networkId: id ?? "", groupId: groupIdFromParams ?? "" }),
        [id, groupIdFromParams]
    );
    const getRowTo = useCallback(
        (row: any, tabType?: string) => getRowToForContext(rowToContext, row, tabType),
        [rowToContext]
    );

    // Preprocess data for controllers tab - merge status and lastActive
    const processTabData = (key: string, data: any[]) => {
        if (getTabType('controllers', data)) {
            return data.map((row: any) => ({
                ...row,
                status: `${row.status} ${row.lastActive}`,
            }));
        }
        return data;
    };

    // Generate breadcrumbs based on context
    const generateBreadcrumbs = () => {
        const networkDetailsTo = id && id !== "undefined" ? `/network-details/${id}` : "";
        
        return [
            {
            to: "/networks",
            label: "All Networks",
                active: false,
            },
            {
                to: networkDetailsTo,
            label: "Network Details",
                active: false,
            },
            {
            to: "",
            label: "Group",
                active: true,
            },
        ];
    };

    // Show "Group not found" state when no group data
    if (!group) {
        return (
            <ContentWrapper isLoading={isPageLoading}>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={6} className="GroupDetails-header">
                                <Row>
                                    <Col>
                                        <h5 className="page-title">{customTitle || groupName || groupIdFromParams || "Group Details"}</h5>
                                    </Col>
                                    {showBreadcrumbs && (
                                        <Col sm="12">
                                            <Breadcrumb className="w-100">
                                            {generateBreadcrumbs().map((item) => (
                                                    <Breadcrumb.Item
                                                    key={item.to || item.label}
                                                        linkAs={item.active ? undefined : Link}
                                                        linkProps={item.active ? undefined : { to: item.to }}
                                                        active={item.active}
                                                    >
                                                        {item.label}
                                                    </Breadcrumb.Item>
                                                ))}
                                            </Breadcrumb>
                                        </Col>
                                    )}
                                </Row>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={8}>
                                <div className="group-not-found-panel" style={{
                                    backgroundColor: '#2f3a51',
                                    borderRadius: '8px',
                                    padding: '24px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{
                                        color: '#ff3860',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px'
                                    }}>
                                        {isPageLoading ? 'Loading group data...' : 'Group not found.'}
                                    </div>
                                    {!isPageLoading && (
                                    <div style={{ color: '#a0a0a0' }}>
                                        The requested group could not be found in this network.
                                    </div>
                                    )}
                                </div>
                            </Col>
                            <Col sm={4}>
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1" style={{ color: '#ffffff' }}>Status</h5>
                                            <div style={{ textAlign: "right" }}>
                                                <span
                                                    style={{
                                                        color: "#ff3860",
                                                        marginRight: 8,
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    &#9679;
                                                </span>
                                                <span style={{ color: '#ffffff' }}>Not found</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
        );
    }

    // Helper to get default column keys for each tab type when no data is available
    const getDefaultColumnKeys = (tabKey: string): string[] => {
        switch (tabKey) {
            case 'sensors':
                return ['name', 'type', 'reading', 'minMax', 'lastactive', 'alert'];
            case 'controllers':
                return ['name', 'type', 'status'];
            default:
                return [];
        }
    };

    // Helper to get data source for column generation (prioritize tabData, fallback to state)
    const getDataForColumns = (tabKey: string) => {
        if (tabData[tabKey] && tabData[tabKey].length > 0) {
            return tabData[tabKey];
        }
        let stateData: any[] = [];
        if (tabKey === 'sensors') stateData = processedSensors;
        else if (tabKey === 'controllers') stateData = processedControllers;
        
        if (stateData && Array.isArray(stateData) && stateData.length > 0) {
            return stateData;
        }
        const defaultKeys = getDefaultColumnKeys(tabKey);
        if (defaultKeys.length > 0) {
            const sampleObject: any = {};
            for (const key of defaultKeys) {
                sampleObject[key] = null;
            }
            return [sampleObject];
        }
        return null;
    };

    const getColumnLabel = (col: string, tabKey: string): React.ReactNode =>
        getNetworkColumnLabel({
            col,
            tabKey,
            tabLabels,
            isRefreshing,
            refreshIcon,
            onRefreshSensors: refreshAllSensorReadings,
            onRefreshControllers: refreshAllControllerStatuses,
        });

    const getColumnRender = (col: string, tabKey: string): ColumnRenderer | undefined => {
        const colLowerForRender = col.toLowerCase();

        if ((colLowerForRender === "name" || colLowerForRender === "sensor_name") && tabKey === "sensors") {
            return createLocalSensorNameRenderer(col, tabKey, handleRowClick, getRowTo);
        }

        if (colLowerForRender === "reading" && tabKey === "sensors") {
            return createReadingRenderer(col);
        }

        if ((colLowerForRender === "minmax" || colLowerForRender.includes("minmax")) && tabKey === "sensors") {
            return createMinMaxRenderer(col);
        }

        if (colLowerForRender === "name" && tabKey === "controllers") {
            return createControllerNameRenderer(col, tabKey, handleRowClick, getRowTo);
        }

        if (colLowerForRender === "lastactive" || colLowerForRender.includes("lastactive")) {
            return createLastActiveRenderer(col, tabKey, handleRefreshSensor);
        }

        if (colLowerForRender === "status") {
            return createStatusRenderer(col, tabKey, handleRefreshController);
        }

        if (colLowerForRender === "alert") {
            return createAlertRenderer(col, handleAlertToggle, returnCheckboxStatus, returnDisabledStatus);
        }

        return (row: any) => row[col];
    };

    // Reusable function to generate columns from data
    const generateColumnsFromData = (data: any[], tabKey: string) => {
        if (!data || data.length === 0) return [];

        return Object.keys(data[0])
            .filter((col: string) => {
                const lower = col.toLowerCase();

                if (tabKey === "controllers" &&
                    (lower === "lastactive" || lower.includes("lastactive") ||
                     lower === "uid" || lower === "said")) {
                    return false;
                }
                if (tabKey === "sensors" &&
                    (lower === "ldsu_uuid" || lower === "said")) {
                    return false;
                }
                if (tabKey === "sensors" && lower === "minmax" && col !== "minMax") {
                    return false;
                }
                if (tabKey === "sensors") {
                    const allowedColumns = ["name", "type", "reading", "minmax", "lastactive", "alert"];
                    return allowedColumns.includes(lower);
                }
                return true;
            })
            .map((col) => {
                const lowerCol = col.toLowerCase();
                const isSensorTab = tabKey === "sensors";
                let sortKey: string | undefined;

                if (isSensorTab) {
                    if (lowerCol === "reading") {
                        sortKey = "readingSortValue";
                    } else if (lowerCol === "minmax" || lowerCol.includes("minmax")) {
                        sortKey = "minMaxSortValue";
                    } else if (lowerCol === "lastactive" || lowerCol.includes("lastactive")) {
                        sortKey = "readings.timestamp";
                    } else if (lowerCol === "alert") {
                        sortKey = "alertSortValue";
                    }
                } else if (tabKey === "controllers") {
                    if (lowerCol === "name" || lowerCol === "type" || lowerCol === "status") {
                        sortKey = col;
                    }
                }

                return {
                    label: getColumnLabel(col, tabKey),
                    key: col,
                    render: getColumnRender(col, tabKey),
                    sortKey,
                };
            });
    };

    const tabData: { [key: string]: any[] } = {
        sensors: filteredSensorList.length > 0 ? filteredSensorList : processedSensors,
        controllers: processTabData('controllers', processedControllers),
    };

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={6} className="GroupDetails-header">
                            <Row>
                                <Col>
                                    <h5 className="page-title">{customTitle || group.name}</h5>
                                </Col>
                                {showBreadcrumbs && (
                                    <Col sm="12">
                                        <Breadcrumb className="w-100">
                                            {generateBreadcrumbs().map((item) => (
                                                <Breadcrumb.Item
                                                    key={item.to || item.label}
                                                    linkAs={item.active ? undefined : Link}
                                                    linkProps={item.active ? undefined : { to: item.to }}
                                                    active={item.active}
                                                >
                                                    {item.label}
                                                </Breadcrumb.Item>
                                            ))}
                                        </Breadcrumb>
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={6}>
                            <div className="group-summary rounded shadow-sm">
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1">Status of Group</h5>
                                            <div style={{ textAlign: "right" }}>
                                                {(() => {
                                                    const isOn = group.status === "On";
                                                    const isOff = group.status === "Off";
                                                    const dotColor = isOn ? "#00d1b2" : "#ff3860";
                                                    let label = "Unknown";
                                                    if (isOff) {
                                                        label = "Off";
                                                    } else if (isOn) {
                                                        label = "On";
                                                    }

                                                    return (
                                                        <>
                                                            <span
                                                                style={{
                                                                    color: dotColor,
                                                                    marginRight: 8,
                                                                }}
                                                            >
                                                                &#9679;
                                                            </span>
                                                            {label}{" "}
                                                            <span style={{ color: "#828fab" }}>
                                                                {renderDateTime(group.timestamp)}
                                                            </span>
                                                        </>
                                                    );
                                                })()}
                                                <IconButton
                                                    disableRipple
                                                    className={
                                                        isRefreshing
                                                            ? "mr-2 ml-2 bigRefresh refresh-animation"
                                                            : "mr-2 ml-2 bigRefresh"
                                                    }
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                    aria-label="Refresh group status"
                                                    onClick={() => {
                                                        updateGroupStatus();
                                                    }}
                                                    onKeyDown={() => updateGroupStatus()}
                                                >
                                                    <img
                                                        src={refreshIcon}
                                                        alt="refresh"
                                                    />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="group-info-group">
                                    <div>
                                        <Form>
                                            <div className="form-box mb-2">
                                                <Form.Row>
                                                    <Col md="12">
                                                        <Form.Label>
                                                            Group Name
                                                        </Form.Label>
                                                    </Col>
                                                    <Col
                                                        lg={10}
                                                        xs={12}
                                                        className="mb-2"
                                                    >
                                                        <Form.Group className="mb-0">
                                                            <Form.Control
                                                                type="text"
                                                                required
                                                                aria-label="group-name"
                                                                defaultValue={groupName}
                                                                disabled={!isGroupNameEditing}
                                                                onChange={(e) => {
                                                                    setGroupName(e.target.value);
                                                                }}
                                                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        toggleGroupNameUpdate();
                                                                    }
                                                                }}
                                                            />
                                                        </Form.Group>
                                                    </Col>

                                                    <Col lg={2} xs="12">
                                                        {isGroupNameEditing ? (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                onClick={toggleGroupNameUpdate}
                                                            >
                                                                Update
                                                            </Button>
                                                        ) : (
                                                            <Button
                                                                type="button"
                                                                className="w-100"
                                                                disabled={true}
                                                                onClick={toggleGroupNameEdit}
                                                            >
                                                                EDIT
                                                            </Button>
                                                        )}
                                                    </Col>
                                                </Form.Row>
                                            </div>
                                        </Form>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6} className="d-flex flex-column">
                            <div className="form-box group-summary rounded shadow-sm" style={{ padding: '0.5rem 30px;' }}>
                                <div className="group-info-group">
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Group UUID</h6>
                                            <h6 className="ginfo float-right">{group?.uuid || '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">No. of Sensors</h6>
                                            <h6 className="ginfo float-right">{group.sensors ?? '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">No. of Actuators</h6>
                                            <h6 className="ginfo float-right">{group.controllers ?? '-'}</h6>
                                        </div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Network Name</h6>
                                            <h6 className="ginfo float-right">{network?.name || '-'}</h6>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <div className="page-tabs groupdetails-tabs mt-3">
                                <Tab.Container
                                    id="group-details-tabs"
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k || "sensors")}
                                >
                                    <Row>
                                        <Col sm={12}>
                                            <Nav className="flex-nowrap" style={{ whiteSpace: 'nowrap' }}>
                                                {tabKeys.map((key) => (
                                                    <Nav.Item key={key} style={{ width: '300px' }}>
                                                        <Nav.Link eventKey={key}>
                                                            <h6>
                                                                {tabLabels[key]}{" "}
                                                                <span className="count">
                                                                    {tabData[key] ? tabData[key].length : 0}
                                                                </span>
                                                            </h6>
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                ))}
                                            </Nav>
                                        </Col>
                                        <Col sm={12}>
                                            <Tab.Content>
                                                {tabKeys.map((key) => (
                                                    <Tab.Pane key={key} eventKey={key}>
                                                        <div style={{ borderRadius: 8, padding: 0 }}>
                                                            {key === 'sensors' && !IS_BASIC_PLAN && (
                                                                <WalletAlertComponent showWarningWhen="LowBalance" />
                                                            )}
                                                            <TabTableWithSearch
                                                                data={tabData[key] || []}
                                                                emptyMessage={getEmptyMessageForTab(key)}
                                                                columns={
                                                                    (() => {
                                                                        // Try to get data from tabData first, then fallback to state
                                                                        const dataForColumns = getDataForColumns(key);
                                                                        if (dataForColumns) {
                                                                            return generateColumnsFromData(dataForColumns, key);
                                                                        }
                                                                        // If no data available at all, return empty array (no headers will show)
                                                                        return [];
                                                                    })()
                                                                }
                                                                placeholder={`Search...`}
                                                                onRowClick={handleRowClick}
                                                                getRowTo={getRowTo}
                                                                tabType={key}
                                                                showArrowColumn={true}
                                                                customRenderers={
                                                                    getTabType('sensors', tabData[key] || [])
                                                                        ? {
                                                                              sensor_name: (value: any, row: any) =>
                                                                                  createLocalSensorNameRenderer("name", "sensors", handleRowClick, getRowTo)(row),
                                                                              reading: renderSensorReadingCell,
                                                                              minMax: renderSensorMinMaxCell,
                                                                              lastactive: renderSensorLastActiveCell,
                                                                              alert: renderSensorAlertCell,
                                                                          }
                                                                        : {}
                                                                }
                                                                customFilters={renderSensorsControllersFilters({
                                                                    tabKey: key,
                                                                    sensorTypes,
                                                                    actuatorTypes,
                                                                    selectedSensorTypes,
                                                                    setSelectedSensorTypes,
                                                                    selectedActuatorTypes,
                                                                    setSelectedActuatorTypes,
                                                                })}
                                                            />
                                                        </div>
                                                    </Tab.Pane>
                                                ))}
                                            </Tab.Content>
                                        </Col>
                                    </Row>
                                </Tab.Container>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
            <LoadingModal showModal={isAlertLoading} />
        </ContentWrapper>
    );
};

export default GroupDetails; 